// Side-effect import: the class below is referenced only as a type, and
// without this the bundler drops the module, the custom element never
// registers, and `hydrate` hands back an un-upgraded <std-accordion>.
import "@tento-ui/components/accordion";
// Side-effect import: the step markers below are std-status-mark elements,
// and the class is otherwise referenced only through its factory.
import "@tento-ui/components/status/status-mark";

import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import type { Accordion } from "@tento-ui/components/accordion";
import { StatusMark } from "@tento-ui/components/status/status-mark";

/** `plan` — the steps the agent declared, drawn by the stepper this repo
 * already owns.
 *
 * It used to be a hand-rolled list of glyph + text rows. `std-accordion`
 * has a `stepper` variant that is precisely this object: a marker column,
 * a rail connecting one marker to the next, and content that hangs under
 * its title clear of the column. Redrawing that by hand meant the
 * transcript's plan looked like nothing else in the app, and the rail —
 * the one thing that makes a sequence read as a sequence — was missing.
 *
 * The typography goes DOWN from the accordion's own scale rather than
 * being invented small. The previous version set 11px marks and 12px text
 * against no baseline at all, which read as cramped rather than dense;
 * this asks the accordion for a smaller marker and tighter padding and
 * otherwise inherits the app's ramp.
 *
 * Status drives the marker glyph and its tone, which is the only semantic
 * this component adds. Steps have no body today — `content` is empty and
 * the stepper renders title-only rows — but the shape is there for when a
 * harness sends per-step detail worth expanding.
 *
 * Honest status: `plan` has TWO records in the whole live corpus. It is
 * the least-exercised primitive here that has any volume at all. */
@component({ name: "std-turn-plan" })
export class TurnPlan extends LonaWebComponent {
  private $steps: Accordion = this.hydrate("steps");

  static makeWith(props: TurnPlan.Props): TurnPlan {
    return (TurnPlan.make() as TurnPlan).bind(props);
  }

  bind(props: TurnPlan.Props): this {
    void this.$steps.bind({
      variant: "stepper",
      items: props.steps.map((step) => ({
        header: step.step,
        marker: TurnPlan.mark(step.status),
        content: "",
      })),
      expandedIndexes: [],
    });
    return this;
  }

  static $styles = [
    css`
      :host {
        display: block;
        width: 100%;
        min-width: 0;
      }
      /* Read by the accordion's own shadow root; a rule aimed at its
         internals from out here would not reach them.

         font-size: 13px used to sit here and did nothing: the stepper's
         header sets --font-size from --accordion-header-font-size, so a
         plain font-size on the host is overwritten a level down. The knob
         is the variable, and the value is a ramp token — a step down from
         the accordion's --h6-size default, not a hand-picked pixel. */
      #steps {
        --accordion-header-font-size: var(--small-size);
        --step-marker-size: 14px;
        --step-pad-x: 6px;
        --step-pad-y: 2px;
        --step-rail-width: 1px;
        --step-gap: 6px;
        /* A plan's steps are one line each, so the circle and its title sit
           on the same centre line. The accordion tops them by default,
           which is right for titles that wrap and wrong here. */
        --step-mark-align: center;
        /* Zero. Revised on review: "space etween steps is too large."

           The accordion's 18px is a gap between SECTIONS, which consecutive
           steps of one plan are not — they are a list, and a list's rhythm
           comes from the rail joining them, not from air. The marker's own
           height and the header's padding already set the pitch, so the
           gap's whole job here was to add to it.

           The knob existed but was inert before this stack: the stepper
           declared its own value a level below the host, so the row pitch
           stayed at the accordion's 41px no matter what was asked for.
           Fixing that in the accordion is what made this block real. */
        --step-spacing: 0px;
        /* The marker is a drawn mark now, not a circle with a character in
           it, so it needs no box of its own to hold type — hence the two
           sizing knobs below rather than a font. */
        --status-mark-box: 14px;
        --status-mark-glyph: 11px;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <std-accordion id="steps"></std-accordion>
  `;
}

export namespace TurnPlan {
  export type StepStatus = "pending" | "running" | "done";

  /** The step's marker, and it is not drawn here.
   *
   * Revised on review: "look at the std task status to see what the icons
   * should look like."
   *
   * This file used to own a small vocabulary of its own — a glyph table, a
   * fill table and a tone table — feeding the accordion's circle. Every one
   * of those was a decision the app had already made somewhere else, and
   * made differently: `std-status-mark` is what this app draws when it
   * shows state, and it draws a HEXAGON for task work specifically, which
   * is what a plan step is. Three parallel tables collapse to one call.
   *
   * The mapping onto its grammar, since it has one:
   *
   *   pending   outline    it has not happened
   *   running   solid      in progress, in the progress tone
   *   done      solid      finished, in the done tone
   *
   * `outline` is documented there as the CONTAINER variant — a thing whose
   * state summarises what it holds — and "not started" is a second reading
   * of the same shape rather than the one that note describes. It is the
   * only way that component says "not yet", and the alternative was keeping
   * a private circle so this file could be precise about a distinction no
   * reader makes. If std-status-mark ever grows a real pending state, this
   * should take it.
   *
   * Note what is NOT here any more: the argument that finished steps must
   * not be green because green means PASSED. That was this file's own
   * reasoning against the app's convention, and the review overruled it.
   * A plan is task work and now looks like the app's task work. */
  export function mark(status: StepStatus): StatusMark {
    return StatusMark.makeWith({
      shape: "hexagon",
      state: status === "done" ? "done" : "progress",
      variant: status === "pending" ? "outline" : "solid",
      label: status,
    });
  }

  export type Step = {
    step: string;
    status: StepStatus;
  };

  export type Props = {
    steps: Step[];
  };
}
